import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@cencosud-cencommerce/paris-uikit';

import { useRepository } from '@/Application/Providers/Repository';
import { useCart } from '@/Application/Selectors/cart/useCart';
import {
  DEFAULT_COMMUNE,
  DEFAULT_REGION,
  INTANGIBLE_KEY,
  URL_ROUTES
} from '@/constants';
import saveBillingAddressForGuest from '@/modules/cart/application/saveBillingAddress/saveBillingAddress';
import { saveShippingAddressForGuest } from '@/modules/cart/application/saveShippingAddressForGuest';
import type { AddressGuest } from '@/modules/cart/domain/AddressGuest';
import type { InvoiceFormFields } from '@/modules/cart/domain/Cart';
import {
  getWeddingID,
  isWeddingFlow
} from '@/modules/shared/utils/isWeddingFlow';
import { useFindLocations } from '@/sections/region/hooks/useFindLocations';
import type { MutatorHook } from '@/sections/shared/types/MutatorHook';

type Entity = AddressGuest | Omit<AddressGuest, 'id'> | InvoiceFormFields;

const isWedding = isWeddingFlow();

const useCreateShippingAddressForGuest = (): MutatorHook<Entity> => {
  const { findCommuneName, findRegionName } = useFindLocations();
  const { cart: cartRepository } = useRepository();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: cartData } = useCart();

  const navigate = useNavigate();
  const isEveryProductIntangible = cartData?.lineItems?.every(
    (item) => item?.productType?.key === INTANGIBLE_KEY
  );

  const trigger = async (address: Entity) => {
    setIsLoading(true);
    try {
      if (isWedding) {
        const regionNumber = Number(address?.region) ?? DEFAULT_REGION;
        const commune = address?.commune ?? DEFAULT_COMMUNE;
        const regionName = findRegionName(regionNumber);
        const communeName = findCommuneName(regionNumber, commune);
        const phoneWithoutSpaces = address?.phone?.replace(/\s/g, '');

        const billingAddress: InvoiceFormFields = {
          email: address?.email ?? '',
          regionName: regionName,
          communeName: communeName,
          commune: address?.commune,
          region: address?.region?.toString() ?? '',
          customerRut: address?.customerRut ?? '',
          firstName: address?.firstName ?? '',
          lastName: address?.lastName ?? '',
          phone: phoneWithoutSpaces ?? ''
        };
        await saveBillingAddressForGuest(cartRepository, billingAddress);

        const weddingId = getWeddingID();
        const billingPath = URL_ROUTES.WEDDING_BILLING_PATH;
        const url = billingPath?.replace(':weddingId', weddingId);
        return navigate(url);
      }

      const addressGuest = address as AddressGuest;

      const res = await saveShippingAddressForGuest(
        cartRepository,
        addressGuest
      );
      if (!res || !res.lineItems) {
        throw new Error('Error al ingresar el usuario');
      }

      if (isEveryProductIntangible) {
        return navigate(URL_ROUTES.BILLING);
      }

      return navigate(URL_ROUTES.SHIPPING);
    } catch (e) {
      toast({
        status: 'error',
        title: 'Error al ingresar el usuario'
      });
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return { trigger, isLoading, error };
};

export default useCreateShippingAddressForGuest;
